import { debounce } from 'lodash';
import { initNumberField } from '@general/helpers';
import { changeCart } from '@services/cart';
import { closeModal, openModal } from './modals';
import {
  addToWishlist,
  updateWishlistBtns,
  deleteFromWishlist,
} from '@components/wishlist';

/**
 * Finds the minicart element within the specified root element.
 *
 * @param {Document|HTMLElement} [root=document] - The root element to search within.
 * @returns {HTMLElement|null} The minicart element if found, otherwise null.
 */
const findMinicart = (root = document) => root.querySelector('#MinicartDrawer');

rebindSwymWishlistButtons();

/**
 * Initializes the minicart by setting up event listeners and initializing line items.
 */
const initMinicart = () => {
  const minicart = findMinicart();
  if (!minicart) return;
  const privacyCheckbox = minicart.querySelector(
    '.privacy-checkbox__container',
  );
  if (
    privacyCheckbox &&
    typeof window.dAgency.initPrivacyCheckbox == 'function'
  )
    window.dAgency.initPrivacyCheckbox(privacyCheckbox);
  minicart.querySelectorAll('.cart-line__wrapper[data-key]').forEach((line) =>
    initLine({
      line,
      beforeFetch: () => {
        minicart.classList.add('loading');
      },
      afterFetch: () => {
        updateMinicart();
        setTimeout(() => {
          updateWishlistBtns();
        }, 400);
      },
    }),
  );
};

/**
 * Updates the minicart content by fetching the latest data and reinitializing the minicart.
 */
const updateMinicart = async () => {
  const minicart = findMinicart();
  if (!minicart) return;
  minicart.classList.add('loading');
  await fetch(minicart.dataset.refreshUrl)
    .then((res) => res.text())
    .then((data) => {
      const minicartSection = new DOMParser().parseFromString(
        data,
        'text/html',
      );
      const fetchMinicart = findMinicart(minicartSection);
      if (fetchMinicart) minicart.innerHTML = fetchMinicart.innerHTML;
      minicart
        .querySelectorAll('.modal__backdrop , .modal__close')
        .forEach((closer) =>
          closer.addEventListener('click', () => closeModal(minicart)),
        );
      initMinicart();
    });
  minicart.classList.remove('loading');
};

/**
 * Initializes a line item in the minicart by setting up event listeners for
 * quantity changes and item removal.
 * @param {Object} options - Options for initializing the line item.
 * @param {HTMLElement} options.line - The line item element.
 * @param {Function} [options.beforeFetch] - Function to call before sending the request.
 * @param {Function} [options.afterFetch] - Function to call after successfully changing the cart item.
 */
const initLine = ({ line, beforeFetch, afterFetch }) => {
  if (!line) return;
  const field = line.querySelector('.cart-line__quantity-value');

  if (field) {
    initNumberField({
      field,
    });

    field.addEventListener(
      'change',
      debounce(() => {
        changeCart({
          key: line.dataset.key,
          quantity: parseInt(field.value),
          callbacks: {
            beforeFetch: beforeFetch,
            afterFetch: afterFetch,
          },
        });
      }, 300),
    );
  }

  const remove = line.querySelector('.cart-line__remove');
  if (remove) {
    remove.addEventListener('click', (e) => {
      e.preventDefault();
      changeCart({
        key: line.dataset.key,
        quantity: 0,
        callbacks: {
          beforeFetch: beforeFetch,
          afterFetch: afterFetch,
        },
      });
    });
  }
};

/**
 * Opens the minicart, optionally loading its content before opening.
 *
 * @param {boolean} [loadBeforeOpen=false] - Whether to load the minicart content before opening it.
 */
const openMinicart = async (loadBeforeOpen = false) => {
  if (loadBeforeOpen) await updateMinicart();
  openModal(findMinicart());
};

function rebindSwymWishlistButtons() {
  const wishlistButtons = document.querySelectorAll(
    '#MinicartDrawer [data-wishlist-btn]',
  );

  if (wishlistButtons) {
    wishlistButtons.forEach((wishlistButton) => {
      const toggleLoading = debounce(() => {
        wishlistButton.classList.toggle('loading');
      }, 500);
      wishlistButton.addEventListener('click', async (e) => {
        e.preventDefault();
        toggleLoading();
        const variantID = wishlistButton?.dataset?.wishlistVariantId;
        const masterID = wishlistButton?.dataset?.wishlistProductId;
        const productURL = wishlistButton?.dataset?.wishlistProductUrl;
        const price = wishlistButton?.dataset?.wishlistPrice;
        const compareAtPrice = wishlistButton?.dataset?.wishlistCompareAtPrice;

        if (wishlistButton?.dataset?.wishlistStatus === 'active') {
          await deleteFromWishlist({
            variantID,
            masterID,
            productURL,
          });
          wishlistButton.dataset.wishlistStatus = 'inactive';
        } else {
          await addToWishlist({
            variantID,
            masterID,
            productURL,
            compareAtPrice,
            price,
          });
          wishlistButton.dataset.wishlistStatus = 'active';
        }
        toggleLoading();
      });
    });
  }
  updateWishlistBtns();
}

// Osserva cambiamenti nel contenuto del drawer (sostituisci #CartDrawer se usi un altro selettore)
const cartDrawerEl = document.querySelector('#MinicartDrawer');

if (cartDrawerEl) {
  const observer = new MutationObserver(() => {
    // Ritarda leggermente per assicurarti che il contenuto sia pronto
    setTimeout(rebindSwymWishlistButtons, 200);
  });

  observer.observe(cartDrawerEl, {
    childList: true,
    subtree: true,
  });
}

/**
 * Closes the minicart.
 */
const closeMinicart = () => closeModal(findMinicart());

export { initMinicart, openMinicart, closeMinicart, initLine };
